import { FilterAltRounded, ReplayRounded, SearchRounded } from "@mui/icons-material";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { useTheme } from "@mui/material/styles";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { IFormValuesJimpiFilter } from "../../../types";
import { JIMPI_FILTER_FORM_INITIAL_VALUE, SEARCH_JIMPI_FORM_VALIDATION_SCHEMA } from "../../../config";
import { FormAutocomplete, FormDateTimePicker, FormTextField } from "../../../../../components/fields";
import { useSetRecoilState } from "recoil";
import { jimpiFilterState } from "../../../recoil";
import { useState } from "react";
import Collapse from "@mui/material/Collapse";
import { BOOLEAN_OPTIONS } from "../../../../../utils";
import { yupResolver } from "@hookform/resolvers/yup";

const JimpiFilters = () => {

    const theme = useTheme();
    const {t} = useTranslation('booking');

    const searchJimpiForm = useForm<IFormValuesJimpiFilter>({
        defaultValues: JIMPI_FILTER_FORM_INITIAL_VALUE,
        resolver: yupResolver(SEARCH_JIMPI_FORM_VALIDATION_SCHEMA)
    });

    const [isFilterExpanded, setIsFilterExpanded] = useState(false);

    const setFilterParams = useSetRecoilState(jimpiFilterState);

    const handleResetForm = (e: React.SyntheticEvent): void => {
        e.stopPropagation();

        searchJimpiForm.reset();

        handleSearch(searchJimpiForm.getValues());
    }

    const handleExpandFilters = (e: React.SyntheticEvent): void => {
        e.stopPropagation();

        setIsFilterExpanded(!isFilterExpanded);
    }

    const handleSearch = (formData: IFormValuesJimpiFilter): void => {
        const filters: any = {};
        for (const [key, value] of Object.entries(formData)) {
            if (!!value) {
                if (Array.isArray(value) && value.length === 0) {
                    continue;
                }

                filters[key] = value;
            }
        }

        setFilterParams(filters);
    }

    return (
        <form noValidate onSubmit={searchJimpiForm.handleSubmit(handleSearch)}>
            <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>
                <FormProvider {...searchJimpiForm}>
                    <Grid container columnSpacing={2}>
                        <Grid item xs={12} md={6} lg={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IFormValuesJimpiFilter> 
                                name={"filename"}
                                label={t('label.fileName')}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormDateTimePicker<IFormValuesJimpiFilter>
                                name="importDateFrom"
                                label={t('label.importDateFrom')}
                            />
                        </Grid>
                        <Grid item xs={12} md={6} lg={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormDateTimePicker<IFormValuesJimpiFilter>
                                name="importDateTo"
                                label={t('label.importDateTo')}
                            />
                        </Grid>

                        <Grid item xs={12} md={6} lg={3} sx={{display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
                            <Button 
                                variant={'text'}
                                color={"accent"}
                                startIcon={<ReplayRounded />}
                                onClick={handleResetForm}
                            >
                                {t('label.reset')}
                            </Button>
                            <Button 
                                variant={"text"}
                                color={"accent"} 
                                startIcon={<FilterAltRounded />} 
                                onClick={handleExpandFilters}
                            >
                                {isFilterExpanded ? t('label.close') : t('label.filter')}
                            </Button>
                            <Button 
                                color={"accent"}
                                size={"icon"}
                                type="submit"
                            >
                                <SearchRounded />
                            </Button>
                        </Grid>
                    </Grid>
                    <Collapse in={isFilterExpanded} timeout="auto" sx={{ width: '100%' }} >
                        <Grid container columnSpacing={2}>
                            <Grid item xs={12} md={6} lg={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormAutocomplete<IFormValuesJimpiFilter>
                                    name="skipped"
                                    label={t('label.skipped')}
                                    options={BOOLEAN_OPTIONS}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormAutocomplete<IFormValuesJimpiFilter>
                                    name="businessError"
                                    label={t('label.businessErrors')}
                                    options={BOOLEAN_OPTIONS}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                />
                            </Grid>
                            <Grid item xs={12} md={6} lg={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormAutocomplete<IFormValuesJimpiFilter>
                                    name="technicalError"
                                    label={t('label.tecnicalErrors')}
                                    options={BOOLEAN_OPTIONS}
                                    isOptionEqualToValue={(option, value) => option?.value === value?.value}
                                />
                            </Grid>
                        </Grid>
                    </Collapse>
                </FormProvider>
            </Paper>
        </form>
    )

}

export default JimpiFilters;